import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import _startCase from 'lodash/startCase';
import { ExternalLink } from 'lucide-react';
import { forwardRef } from 'react';
import type { LabelProps } from 'semantic-ui-react';
import { Icon, Label, Popup } from 'semantic-ui-react';
import { z } from 'zod';

import { AnchoreLink } from '@components/anchore-link';
import { darkReaderInvertClass } from '@components/dark-mode/dark-mode-provider';
import type { CvssAssessment } from '@features/applications/applications.models';
import { CvssSourceCategory } from '@features/applications/applications.models';

import type { ScoreType } from './cvss';
import { getCvssRating } from './cvss';
import type { CvssRating, VulnerabilitySeverity } from './severity.const';
import { COLOR_BY_SEVERITY, CVSS_RANGES } from './severity.const';
import s from './severity-label.scss';

export type { VulnerabilitySeverity };

const uuidSchema = z.string().uuid();

export interface SeverityLabelProps extends Pick<
  LabelProps,
  'className' | 'size'
> {
  /**
   * CVSS assessment to display. The v3 rating - `v3Severity`, or `v3Score`
   * banded when absent - drives the trigger badge and the primary popup row;
   * v2 and v4 read their own rating and render as secondary rows. The
   * assessment's `category` and `source` populate the badge icon and the
   * source footer, and its `sourceUrl` drives the primary row's link.
   */
  cvssAssessment?: CvssAssessment | null;
  /**
   * Severity level enum. Accepts any casing. Read by the basic variant only
   * - the enhanced badge always derives its rating from `cvssAssessment`.
   */
  severity?:
    | VulnerabilitySeverity
    | Lowercase<VulnerabilitySeverity>
    | Uppercase<VulnerabilitySeverity>
    | null;
  /**
   * Vulnerability identifier (e.g. CVE-2024-1234). When provided and the
   * assessment has no `sourceUrl`, an AnchoreLink to the Anchore vulnerability
   * page is rendered in the popup's primary CVSS row.
   */
  vulnerabilityId?: string | null;
  /**
   * Display variant. `'enhanced'` always renders the CVSS-style layout
   * (badge + dot + score), even when CVSS data is absent.
   * @default 'basic'
   */
  variant?: 'basic' | 'enhanced';
}

/**
 * Maps a category to its badge icon or styled text.
 */
const CategoryBadgeIcon = ({
  category,
}: {
  category: CvssSourceCategory | null | undefined;
}) => {
  switch (category) {
    case CvssSourceCategory.Github:
      return (
        <Icon aria-label="GitHub" className={s.githubIcon} name="github" />
      );
    case CvssSourceCategory.Nvd:
      return <span role="img" aria-label="NVD" className={s.nvdIcon} />;
    case CvssSourceCategory.Cisa:
      return <span className={s.sourceText}>CISA</span>;
    default:
      return <span className={s.sourceText}>Other</span>;
  }
};

/**
 * Formats a raw source string for display in the popup. Returns the string
 * as-is unless it looks like a UUID, in which case it falls back to "Other".
 */
const formatSource = (source: string): string =>
  uuidSchema.safeParse(source).success ? 'Other' : source;

/**
 * Formats a CVSS score for display, dropping the decimal at the 10.0 max -
 * the widest possible value - since there's no finer precision above it.
 */
const formatCvssScore = (score: number): string =>
  score === 10 ? '10' : score.toFixed(1);

/**
 * Resolves a CVSS rating's dot/pill color, borrowing `negligible`'s for
 * `none` - CVSS's own scale has no `negligible` tier.
 */
const colorForRating = (rating: CvssRating) =>
  COLOR_BY_SEVERITY[rating === 'none' ? 'negligible' : rating];

/**
 * CVSS score types displayed below the primary row, in display order.
 */
const SECONDARY_SCORE_TYPES: ScoreType[] = ['v4', 'v2'];

/**
 * A preset label component to display the severity of a vulnerability.
 *
 * When `cvssAssessment` is provided (or `variant` is `'enhanced'`), renders
 * a compact badge with category icon, severity dot, and score — wrapped in a
 * hover popup with details. Otherwise, renders the original colored severity
 * pill.
 */
export const SeverityLabel = forwardRef<HTMLDivElement, SeverityLabelProps>(
  (
    {
      className,
      cvssAssessment,
      severity: severityProp,
      size = 'small',
      vulnerabilityId,
      variant = 'basic',
    },
    forwardedRef,
  ) => {
    useStyles(s);

    const category = cvssAssessment?.category ?? null;
    const primaryCvssScore = cvssAssessment?.v3Score ?? null;
    const source = cvssAssessment?.source ?? null;
    const sourceUrl = cvssAssessment?.sourceUrl ?? null;
    const rating = getCvssRating(cvssAssessment, 'v3');

    const isEnhanced =
      variant === 'enhanced' ||
      primaryCvssScore != null ||
      !!category ||
      rating !== 'unknown';

    // Simple mode: no CVSS data, render the original colored pill
    if (!isEnhanced) {
      const severity = severityProp ?? 'Unknown';
      const lowercaseSeverity =
        severity.toLowerCase() as Lowercase<VulnerabilitySeverity>;
      const uppercaseSeverity =
        severity.toUpperCase() as Uppercase<VulnerabilitySeverity>;
      const color = COLOR_BY_SEVERITY[lowercaseSeverity];

      return (
        <Label
          ref={forwardedRef}
          className={classNames(className, {
            [darkReaderInvertClass]: !color,
          })}
          color={color}
          content={uppercaseSeverity}
          size={size}
        />
      );
    }

    const color = colorForRating(rating);

    // Enhanced mode: single label with category icon + dot + score, with popup
    const trigger = (
      <Label
        ref={forwardedRef}
        className={classNames(s.enhanced, className)}
        size={size}
      >
        <span className={s.sourceDivider}>
          <CategoryBadgeIcon category={category} />
        </span>

        <Icon className={s.dot} color={color} name="circle" />

        <span className={s.score}>
          {primaryCvssScore != null ? formatCvssScore(primaryCvssScore) : 'N/A'}
        </span>
      </Label>
    );

    const cvssRange = CVSS_RANGES[rating];

    const renderScoreRow = (scoreType: ScoreType) => {
      const score = cvssAssessment?.[`${scoreType}Score`] ?? null;
      const rowRating = getCvssRating(cvssAssessment, scoreType);
      const rowColor = colorForRating(rowRating);
      return (
        <div className={s.popupRow} key={scoreType}>
          <span className={s.popupVersionPill}>{`CVSS ${scoreType}.0`}</span>
          {score == null ? (
            <span className={s.popupRowMissing}>No score available</span>
          ) : (
            <span className={s.popupRowEnd}>
              <Icon className={s.dot} color={rowColor} name="circle" />
              <span className={s.popupRowSeverity}>
                {_startCase(rowRating)}
              </span>
              <span className={s.popupRowScore}>{formatCvssScore(score)}</span>
            </span>
          )}
        </div>
      );
    };

    const sourceLink = sourceUrl ? (
      <a
        className={s.popupSourceLink}
        href={sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="View source for CVSS v3.x"
      >
        <CategoryBadgeIcon category={category} />
        <ExternalLink className={s.popupSourceLinkIcon} />
      </a>
    ) : vulnerabilityId ? (
      <span className={s.popupAnchoreLink}>
        <AnchoreLink vulnerabilityId={vulnerabilityId} />
      </span>
    ) : null;

    return (
      <Popup
        className={s.popup}
        position="bottom left"
        trigger={trigger}
        on="hover"
        hoverable
      >
        <Popup.Content>
          <div className={s.popupContent}>
            <div className={s.popupHeader}>
              <span className={s.popupSeverity}>
                <Icon className={s.dot} color={color} name="circle" />
                <span className={s.popupSeverityLabel}>
                  {_startCase(rating)}
                </span>
              </span>
              <span className={s.popupHeaderScore}>
                {primaryCvssScore != null
                  ? formatCvssScore(primaryCvssScore)
                  : 'N/A'}
              </span>
            </div>

            <div className={classNames(s.popupRow, s.popupRowPrimary)}>
              <span
                className={classNames(
                  s.popupVersionPill,
                  s.popupVersionPillPrimary,
                )}
              >
                CVSS v3.x
              </span>
              <span className={s.popupRangeText}>{cvssRange ?? ' '}</span>
              {sourceLink}
            </div>

            <div className={s.popupDivider} />
            {SECONDARY_SCORE_TYPES.map(renderScoreRow)}

            {source && (
              <div className={s.popupSourceFooter}>
                Source: {formatSource(source)}
              </div>
            )}
          </div>
        </Popup.Content>
      </Popup>
    );
  },
);
SeverityLabel.displayName = 'SeverityLabel';

export default SeverityLabel;
